import React, { useState } from 'react';
import DailySummary, { DailySummaryData } from './DailySummary';
import ArticleSummary from './ArticleSummary';
import { Article } from '../../config/api';
import './DaySection.css';

export interface DaySectionProps {
  dailySummary: DailySummaryData;
  articles: Article[];
  defaultExpanded?: boolean;
  showThemes?: boolean;
  showStats?: boolean;
  showArticleTags?: boolean;
  className?: string;
}

const DaySection: React.FC<DaySectionProps> = ({
  dailySummary,
  articles,
  defaultExpanded = true,
  showThemes = true,
  showStats = true,
  showArticleTags = false,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Temporarily show all articles without highlighting filter
  const highlightedArticles: Article[] = [];
  const regularArticles = articles; // Show all articles as regular articles

  return (
    <section className={`day-section ${className}`}>
      {/* Daily Summary */}
      <DailySummary
        dailySummary={dailySummary}
        showThemes={showThemes}
        showStats={showStats}
      />

      {/* Articles Section */}
      <div className="day-section__articles">
        <header 
          className="day-section__articles-header"
          onClick={toggleExpanded}
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '8px',
            padding: '20px 24px',
            margin: '-20px -24px 20px -24px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Click to collapse articles' : 'Click to expand articles'}
          title={isExpanded ? 'Click to collapse articles' : 'Click to expand articles'}
        >
          <h3 className="day-section__articles-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            margin: 0 
          }}>
            Today's Articles ({articles.length})
            <span style={{ 
              fontSize: '12px', 
              color: '#6c757d', 
              fontWeight: 'normal',
              opacity: 0.7
            }}>
              {isExpanded ? '(click to collapse)' : '(click to expand)'}
            </span>
          </h3>
          
          <svg
            className={`day-section__toggle-icon ${isExpanded ? 'day-section__toggle-icon--expanded' : ''}`}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ 
              transition: 'transform 0.2s ease',
              color: '#667eea'
            }}
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </header>

        {/* Articles List */}
        <div className={`day-section__articles-content ${isExpanded ? 'day-section__articles-content--expanded' : ''}`}>
          {/* Highlighted Articles */}
          {highlightedArticles.length > 0 && (
            <div className="day-section__highlighted-articles">
              <h4 className="day-section__section-title">Featured Stories</h4>
              <div className="day-section__articles-list">
                {highlightedArticles.map((article) => (
                  <ArticleSummary
                    key={article.id}
                    article={article}
                    showSource={true}
                    showTags={showArticleTags}
                    isHighlighted={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Articles */}
          {regularArticles.length > 0 && (
            <div className="day-section__regular-articles">
              {highlightedArticles.length > 0 && (
                <h4 className="day-section__section-title">More Stories</h4>
              )}
              <div className="day-section__articles-list">
                {regularArticles.map((article) => (
                  <ArticleSummary
                    key={article.id}
                    article={article}
                    showSource={true}
                    showTags={showArticleTags}
                    isHighlighted={false}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {articles.length === 0 && (
            <div className="day-section__empty-state">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              <p>No articles available for this day.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DaySection;
